import {
  normalizeOutput,
  type ExampleResult,
  type ExampleStatus,
  type ExerciseExample,
} from '@pebblecode/shared';

import { runProgram, stdinLines } from './interpreter.js';
import type { Program } from './ir.js';

/** Steps allowed per example run; a loop that never ends stops here. */
export const EXAMPLE_STEP_LIMIT = 500_000;

/**
 * Runs a program on every example of an exercise. The student sees these results next to each
 * example, so it is the same comparison the server uses to mark the exercise solved.
 */
export function checkExamples(
  program: Program,
  examples: readonly ExerciseExample[],
): { passed: number; total: number; results: ExampleResult[] } {
  const results = examples.map((ex): ExampleResult => {
    const r = runProgram(program, stdinLines(ex.input), EXAMPLE_STEP_LIMIT);
    const status: ExampleStatus =
      r.status === 'error'
        ? 'error'
        : r.status === 'waiting-input'
          ? 'needs-input'
          : r.status !== 'finished'
            ? 'too-long'
            : normalizeOutput(r.output) === normalizeOutput(ex.output)
              ? 'ok'
              : 'wrong-output';
    return { input: ex.input, expected: ex.output, actual: r.output, status, error: r.error };
  });
  return {
    passed: results.filter((r) => r.status === 'ok').length,
    total: results.length,
    results,
  };
}
